import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Zap,
  Star,
  PhoneCall,
  Timer,
  UserCheck,
  Home,
  Volume2,
  Mic
} from 'lucide-react';

import WhyChooseUs from '../components/WhyChooseUs';
import Benefits from '../components/Benefits/Benefits';
import Testimonials from '../components/Testimonials/Testimonials';
import BookingForm from '../components/BookingForm';
import { trackCallConversion } from '../utils/tracking';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/toaster';

const Link = ({ href, to, children, className, ...props }) => {
  return (
    <RouterLink to={href || to || '#'} className={className} {...props}>
      {children}
    </RouterLink>
  );
};

const fadeInUpViewport = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0 },
};

const fadeInLeftViewport = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0 },
};

const fadeInRightViewport = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0 },
};

const staggerContainerViewport = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const faqs = [
  {
    question: 'How much does iPhone microphone and speaker repair cost in Delhi NCR?',
    answer:
      'Ear speaker / microphone cleaning and mesh restoration starts from ₹699, while full OEM earpiece, loudspeaker, or mic module replacement starts from ₹1,299 depending on your iPhone model.',
  },
  {
    question: 'Why can callers barely hear me during phone calls?',
    answer:
      'This happens when the bottom primary microphone or noise-cancellation mic is clogged with dust or damaged. We perform acoustic micro-cleaning or replace the mic flex cable right at your doorstep.',
  },
  {
    question: 'My iPhone ear speaker sound is very low. Do I need a replacement or cleaning?',
    answer:
      'Over 70% of low earpiece volume cases are resolved through our deep acoustic sonic cleaning and mesh replacement without needing a new speaker module. If the coil is blown, we replace it with a genuine OEM speaker.',
  },
  {
    question: 'How long does doorstep iPhone mic and speaker repair take?',
    answer:
      'Acoustic cleaning takes 15 to 20 minutes, while full speaker/mic module replacement takes about 30 minutes at your doorstep.',
  },
  {
    question: 'Is there a warranty on iPhone speaker and mic repairs?',
    answer:
      'Yes, we provide up to 1 Year Warranty on all OEM speaker and microphone module replacements for loud, distortion-free sound.',
  },
  {
    question: 'Will my iPhone data remain safe during audio repairs?',
    answer:
      'Yes, 100%. Audio repairs are entirely external and modular. No data is altered, erased, or accessed during the repair process.',
  },
];

function BeforeAfterAudioCards() {
  return (
    <div className="relative w-full max-w-[560px] mx-auto select-none">
      {/* Floating Badges */}
      <div className="hidden sm:flex items-center gap-1.5 absolute -top-3.5 right-4 z-30 bg-[#0F172A]/90 backdrop-blur-md border border-[#F5B93A]/50 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
        <Sparkles className="w-3.5 h-3.5 text-[#F5B93A]" />
        <span>HD Stereo Audio & Clear Voice Restored</span>
      </div>

      <div className="hidden sm:flex items-center gap-1.5 absolute -bottom-3.5 left-4 z-30 bg-[#FFFDF9]/95 backdrop-blur-md border border-[#C99E54]/40 text-[#0F172A] text-xs font-bold px-3 py-1 rounded-full shadow-lg">
        <ShieldCheck className="w-4 h-4 text-[#D99215]" />
        <span>upto 1 Year Warranty on Audio Modules</span>
      </div>

      {/* Main Dual Panel Comparison Card */}
      <div className="bg-[#FFFDF9] dark:bg-[#0c1828] p-2.5 sm:p-3.5 rounded-[22px] sm:rounded-[28px] border-2 border-[#C99E54]/35 shadow-[0_16px_45px_rgba(0,0,0,0.35),0_0_0_1px_rgba(245,185,58,0.15)] overflow-hidden">
        <div className="relative rounded-[16px] sm:rounded-[20px] overflow-hidden bg-slate-900 border border-slate-700/50 shadow-inner aspect-[4/3] group">
          <img
            src="/images/iphone_audio_before_after.jpg"
            alt="Before and After iPhone Mic and Speaker Repair - DSMR Solutions"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            loading="eager"
          />

          {/* Left Panel Badges & Watermark */}
          <div className="absolute top-2.5 sm:top-3.5 left-2.5 sm:left-3.5 z-20 flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-extrabold tracking-wide bg-[#00C2CB] text-white shadow-md">
              Before
            </span>
          </div>
          <div className="absolute top-3 sm:top-4 left-1/4 -translate-x-1/2 text-center pointer-events-none z-10 pl-6 sm:pl-8">
            <span className="text-[9px] sm:text-xs font-bold text-slate-800/70 dark:text-slate-200/60 tracking-tight drop-shadow-sm">
              DSMR Solutions
            </span>
          </div>

          {/* Right Panel Badges & Watermark */}
          <div className="absolute top-2.5 sm:top-3.5 left-[52%] sm:left-[52%] z-20 flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-extrabold tracking-wide bg-[#22C55E] text-white shadow-md">
              After
            </span>
          </div>
          <div className="absolute top-3 sm:top-4 left-3/4 -translate-x-1/2 text-center pointer-events-none z-10 pl-6 sm:pl-8">
            <span className="text-[9px] sm:text-xs font-bold text-slate-800/70 dark:text-slate-200/60 tracking-tight drop-shadow-sm">
              DSMR Solutions
            </span>
          </div>

          {/* Center Subtle Divider Line */}
          <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-white/20 pointer-events-none" />

          {/* Bottom Labels */}
          <div className="absolute bottom-2.5 left-2.5 right-2.5 flex justify-between gap-3 pointer-events-none">
            <div className="bg-black/60 backdrop-blur-sm rounded-lg py-1 px-2.5 border border-white/10 text-center flex-1 max-w-[48%]">
              <span className="text-[9px] sm:text-[11px] font-bold text-rose-300 block truncate">
                ❌ Muffled Sound & Dead Mic
              </span>
            </div>
            <div className="bg-black/60 backdrop-blur-sm rounded-lg py-1 px-2.5 border border-white/10 text-center flex-1 max-w-[48%]">
              <span className="text-[9px] sm:text-[11px] font-bold text-emerald-300 block truncate">
                ✨ Crystal-Clear HD Sound
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Bottom Highlights */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 sm:mt-4 text-center">
        <div className="p-2 sm:p-2.5 rounded-xl bg-[#FFFDF9]/80 border border-[#C99E54]/20 shadow-sm">
          <div className="text-[11px] sm:text-xs text-rose-600 font-bold">❌ Before Repair</div>
          <div className="text-[10px] sm:text-xs text-slate-600 mt-0.5">Low call volume, crackling, mic silent</div>
        </div>
        <div className="p-2 sm:p-2.5 rounded-xl bg-[#FFFDF9]/80 border border-[#C99E54]/20 shadow-sm">
          <div className="text-[11px] sm:text-xs text-emerald-600 font-bold">✓ Doorstep Restored</div>
          <div className="text-[10px] sm:text-xs text-slate-600 mt-0.5">Loud stereo clarity, noise-free calls</div>
        </div>
      </div>
    </div>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <motion.section
      className="bg-gradient-to-br from-[#FEFAF4] via-[#FDF9F3] to-[#FEFAF6] border border-[#C99E54]/25 shadow-[inset_0_1px_2px_rgba(255,255,255,0.95),0_10px_24px_rgba(191,160,110,0.10)] rounded-2xl sm:rounded-3xl px-4 sm:px-6 pt-8 sm:pt-10 pb-10 sm:pb-14 flex flex-col items-center my-8 sm:my-16 relative overflow-hidden"
      id="faq-section"
      variants={staggerContainerViewport}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.2 }}
    >
      <motion.div
        variants={fadeInUpViewport}
        className="border border-[#C99E54]/30 bg-[#C99E54]/10 rounded-full shadow-sm inline-flex items-center text-[#C98E32] font-bold text-xs sm:text-sm uppercase tracking-widest px-4 sm:px-5 py-2 mb-4 sm:mb-5 mt-4 select-none pointer-events-none"
      >
        <span role="img" aria-label="faq">
          ❓
        </span>
        &nbsp; FAQS
      </motion.div>
      <motion.h2
        variants={fadeInUpViewport}
        className="text-xl sm:text-2xl md:text-3xl font-extrabold text-center mb-2 tracking-tight text-[#0F172A] px-2"
      >
        Frequently Asked Questions
      </motion.h2>
      <motion.p
        variants={fadeInUpViewport}
        className="text-sm sm:text-base md:text-[1.05rem] text-[#475569] text-center mb-6 sm:mb-9 max-w-[600px] px-2"
      >
        Find quick answers to common questions about our iPhone microphone and speaker repair services.
      </motion.p>
      <div className="w-full flex flex-col items-center gap-3 sm:gap-4">
        {faqs.map((faq, idx) => (
          <motion.div
            className={`bg-[#FFFDF9] w-full max-w-[800px] rounded-lg sm:rounded-xl border border-[#C99E54]/20 hover:border-[#C99E54]/40 shadow-[0_4px_14px_rgba(191,160,110,0.08)] px-4 sm:px-5 pt-4 sm:pt-5 pb-2 cursor-pointer transition-all duration-200 overflow-hidden ${
              openIdx === idx ? 'pb-4 sm:pb-5 border-[#C99E54]/50' : ''
            }`}
            key={`faq-${idx}`}
            variants={idx % 2 === 0 ? fadeInLeftViewport : fadeInRightViewport}
            onClick={() => setOpenIdx(openIdx === idx ? -1 : idx)}
          >
            <div className="flex justify-between items-start gap-2">
              <span className="text-sm sm:text-base font-semibold text-[#0F172A] flex-1">{faq.question}</span>
              <span className="text-base sm:text-lg text-[#C98E32] font-bold select-none flex-shrink-0">
                {openIdx === idx ? '▲' : '▼'}
              </span>
            </div>
            <div
              className="text-sm sm:text-base text-[#334155] mt-2 leading-relaxed transition-[max-height,opacity] duration-500 ease-in-out overflow-hidden"
              style={{ maxHeight: openIdx === idx ? '200px' : '0', opacity: openIdx === idx ? 1 : 0 }}
            >
              {faq.answer}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}

function HeroSlider({ onBookClick }) {
  return (
    <section className="relative overflow-hidden bg-[#030E1C] text-white py-10 sm:py-14 lg:py-16 border-b border-[#C99E54]/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
          
          {/* LEFT COLUMN: Main Content */}
          <div className="flex flex-col items-start text-left space-y-4 sm:space-y-5">
            {/* Top Pill Tag */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#F5B93A]/15 via-[#E8A020]/20 to-[#F5B93A]/10 border border-[#F5B93A]/40 text-[#F5B93A] text-xs sm:text-sm font-semibold tracking-wide shadow-sm">
              <Zap className="w-4 h-4 text-[#F5B93A]" />
              <span>SAME-DAY DOORSTEP iPHONE MIC & SPEAKER REPAIR</span>
            </div>

            {/* Main Title */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold leading-[1.18] tracking-tight text-white">
              iPhone Mic & Speaker Repair in Delhi NCR –{' '}
              <span className="bg-gradient-to-r from-[#F5B93A] via-[#F3A71A] to-[#D99215] bg-clip-text text-transparent">
                Muffled Voice? Fixed in 20 Mins.
              </span>
            </h1>

            {/* Subtitle / Description */}
            <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">
              Fix low earpiece volume, crackling loudspeaker, and muffled microphone at your doorstep. 
              Original OEM acoustic audio modules with up to 1-Year Warranty.
            </p>

            {/* Features Row - 4 Circular Icon Badges */}
            <div className="grid grid-cols-4 gap-2 sm:gap-4 w-full max-w-lg py-2">
              {/* Item 1: Same Day */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[#F5B93A]/50 bg-[#071322] flex items-center justify-center text-[#F5B93A] mb-1.5 shadow-[0_2px_10px_rgba(245,185,58,0.15)] group-hover:border-[#F5B93A] transition-all">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="text-xs sm:text-sm font-bold text-white leading-tight">Same Day</div>
                <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">Service</div>
              </div>

              {/* Item 2: 20 Mint */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[#F5B93A]/50 bg-[#071322] flex items-center justify-center text-[#F5B93A] mb-1.5 shadow-[0_2px_10px_rgba(245,185,58,0.15)] group-hover:border-[#F5B93A] transition-all">
                  <Timer className="w-5 h-5" />
                </div>
                <div className="text-xs sm:text-sm font-bold text-white leading-tight">20 Mint</div>
                <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">Avg Repair Time</div>
              </div>

              {/* Item 3: Verified */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[#F5B93A]/50 bg-[#071322] flex items-center justify-center text-[#F5B93A] mb-1.5 shadow-[0_2px_10px_rgba(245,185,58,0.15)] group-hover:border-[#F5B93A] transition-all">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div className="text-xs sm:text-sm font-bold text-white leading-tight">Audio Expert</div>
                <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">Technician</div>
              </div>

              {/* Item 4: Doorstep */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[#F5B93A]/50 bg-[#071322] flex items-center justify-center text-[#F5B93A] mb-1.5 shadow-[0_2px_10px_rgba(245,185,58,0.15)] group-hover:border-[#F5B93A] transition-all">
                  <Home className="w-5 h-5" />
                </div>
                <div className="text-xs sm:text-sm font-bold text-white leading-tight">Doorstep</div>
                <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">Service</div>
              </div>
            </div>

            {/* Starting Price Banner */}
            <div className="flex items-center justify-between gap-4 w-full max-w-lg bg-gradient-to-r from-[#142338] via-[#0F1D2E] to-[#142338] border border-[#F5B93A]/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-md">
              <div>
                <span className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-400 font-semibold block">Speaker & Mic Fix Starts At</span>
                <span className="text-xl sm:text-2xl font-black text-white">
                  ₹699* <span className="text-xs font-normal text-slate-400">/ depends on cleaning or module</span>
                </span>
              </div>
              <div className="text-right">
                <span className="inline-block text-[11px] sm:text-xs font-bold text-[#F5B93A] bg-[#F5B93A]/15 border border-[#F5B93A]/30 px-2.5 py-1 rounded-full">
                  Pay After Repair
                </span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto pt-1">
              <Button
                size="lg"
                onClick={onBookClick}
                className="w-full sm:w-auto bg-gradient-to-r from-[#F5B93A] via-[#E8A020] to-[#D99215] hover:from-[#f0b040] hover:to-[#c8881a] text-slate-950 font-bold rounded-xl shadow-[0_4px_20px_rgba(217,146,21,0.4)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_25px_rgba(217,146,21,0.55)] border border-[#F5B93A]/50 py-3.5 px-7 text-base cursor-pointer"
              >
                Book Your Repair
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#0F1D2E] hover:bg-[#16273c] text-white font-semibold rounded-xl transition-all duration-300 hover:-translate-y-1 shadow-md border border-slate-700 hover:border-[#F5B93A]/40 py-3.5 px-6 text-base"
                asChild
              >
                <a
                  href="tel:+918700319769"
                  data-dsmr-phone="true"
                  onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
                  className="flex items-center justify-center"
                >
                  <PhoneCall className="mr-2 w-4 h-4 text-[#F5B93A]" />
                  Call Now : 8700319769
                </a>
              </Button>
            </div>

            {/* Trust Indicator */}
            <div className="flex items-center gap-2 pt-1 text-xs text-slate-400">
              <div className="flex text-[#F5B93A]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#F5B93A]" />
                ))}
              </div>
              <span>4.7/5 Rating by 3,100+ iPhone Customers in Delhi NCR</span>
            </div>
          </div>

          {/* RIGHT COLUMN: Side-by-Side Before & After Visual Cards */}
          <div className="w-full flex justify-center items-center lg:justify-end">
            <BeforeAfterAudioCards />
          </div>

        </div>
      </div>
    </section>
  );
}

function SliderShowcase() {
  const showcaseRef = useRef(null);

  const scrollByAmount = (amount) => {
    const el = showcaseRef.current;
    if (!el) return;

    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const items = [
    { key: 'ip17', label: 'iPhone 17', img: '/mobiles/iphone/iphone17/iphone_17.webp', alt: 'iPhone 17 mic and speaker repair in Delhi NCR' },
    { key: 'ip16', label: 'iPhone 16', img: '/mobiles/iphone/iphone16/iphone_16.webp', alt: 'iPhone 16 mic and speaker repair in Delhi NCR' },
    { key: 'ip15', label: 'iPhone 15', img: '/mobiles/iphone/iphone15/iphone_15.webp', alt: 'iPhone 15 mic and speaker repair in Delhi NCR' },
    { key: 'ip14', label: 'iPhone 14', img: '/mobiles/iphone/iphone14/iphone_14.webp', alt: 'iPhone 14 mic and speaker repair in Delhi NCR' },
    { key: 'ip13', label: 'iPhone 13', img: '/mobiles/iphone/iphone13/iphone_13.webp', alt: 'iPhone 13 mic and speaker repair in Delhi NCR' },
    { key: 'ip12', label: 'iPhone 12', img: '/mobiles/iphone/iphone12/iphone_12.webp', alt: 'iPhone 12 mic and speaker repair in Delhi NCR' },
    { key: 'ip11', label: 'iPhone 11', img: '/mobiles/iphone/iphone11/iphone_11.webp', alt: 'iPhone 11 mic and speaker repair in Delhi NCR' },
    { key: 'ipx', label: 'iPhone X / XS', img: '/mobiles/iphone/iphonex/iphone_x.webp', alt: 'iPhone X mic and speaker repair in Delhi NCR' },
    { key: 'ip8', label: 'iPhone 8 / SE', img: '/mobiles/iphone/old_iphone.webp', alt: 'iPhone 8 mic and speaker repair in Delhi NCR' },
  ];

  return (
    <div className="mt-4 flex items-center gap-2 sm:gap-3 showcaseWrap">
      <button
        type="button"
        className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-[#FFFDF9] border border-[#C99E54]/30 hover:bg-[#F7EFE3] text-[#C98E32] transition-all shadow-sm hover:scale-105 active:scale-95 cursor-pointer"
        onClick={() => scrollByAmount(-280)}
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
      <div ref={showcaseRef} className="flex-1 min-w-0 flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar scroll-smooth py-2 iphoneShowcase">
        {items.map((item) => {
          const slug = (item.label || item.key)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          const href = `/apple/${slug}`;

          return (
            <div key={item.key} className="flex-shrink-0 iphoneModel group">
              <Link href={href} aria-label={`${item.label} mic and speaker repair in Delhi NCR`} className="flex flex-col items-center gap-1 sm:gap-2">
                <div
                  className="w-[60px] h-[75px] sm:w-[80px] sm:h-[100px] rounded-xl bg-gradient-to-br from-[#FFFFFF] to-[#FEFAF4] border border-[#C99E54]/25 shadow-[0_4px_16px_rgba(191,160,110,0.15)] group-hover:shadow-[0_8px_24px_rgba(191,160,110,0.25)] group-hover:border-[#C99E54]/50 flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1"
                >
                  <img
                    src={item.img}
                    alt={item.alt || item.label}
                    width={75}
                    height={100}
                    className="object-contain deviceImg"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-[#0F172A] group-hover:text-[#C98E32] text-center transition-colors">{item.label}</span>
              </Link>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-[#FFFDF9] border border-[#C99E54]/30 hover:bg-[#F7EFE3] text-[#C98E32] transition-all shadow-sm hover:scale-105 active:scale-95 cursor-pointer"
        onClick={() => scrollByAmount(280)}
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
    </div>
  );
}

export default function IphoneMicSpeakerRepairDelhiNcr() {
  const handleBookClick = () => {
    const target = document.querySelector('#booking-form');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col iphonefixPage bg-gradient-to-br from-[#F9F4EE] via-[#FBF6F1] to-[#FDF9F4] text-[#0F172A] relative overflow-hidden font-['Outfit','Inter',sans-serif]">
      {/* Background ambient radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[480px] bg-[radial-gradient(ellipse_at_center,rgba(235,205,155,0.28)_0%,rgba(250,246,240,0)_70%)] pointer-events-none z-0" aria-hidden="true" />

      <div className="relative z-10 flex flex-col">
        <HeroSlider onBookClick={handleBookClick} />

        <section className="py-8 sm:py-12 iphoneModelsSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 modelsContent">
            <div className="modelsLeft">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-2 text-[#0F172A] tracking-tight">iPhone Models We Repair Mic & Speakers For</h2>
              <p className="text-sm sm:text-base text-[#475569] mb-4">
                Earpiece speaker, loudspeaker, and microphone replacement for all models from iPhone 6 to iPhone 17 Pro Max
              </p>
              <SliderShowcase />
            </div>
          </div>
        </section>

        <section>
          <WhyChooseUs />
        </section>

        <section>
          <Benefits />
        </section>

        <section>
          <Testimonials />
        </section>

        <section id="booking-form" className="py-2 sm:py-4 bookingFormSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <BookingForm />
          </div>
        </section>

        <section className="py-2 sm:py-4 faqSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <FAQSection />
          </div>
        </section>

        <section className="py-8 sm:py-10 localSeoSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-6 sm:mb-8 localSeoHeader">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#0F172A] tracking-tight px-2">iPhone Mic & Speaker Repair Services Across Delhi NCR - Complete Coverage</h2>
              <p className="text-sm sm:text-base text-[#475569] localSeoSubtitle mt-2 px-2">
                Doorstep iPhone microphone, earpiece, and loudspeaker repair in all major areas of Delhi, Gurgaon, Noida, and Faridabad
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 areasGrid">
              <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FDF8F0] border border-[#C99E54]/25 shadow-[0_6px_20px_rgba(191,160,110,0.10)] rounded-xl sm:rounded-2xl p-4 sm:p-5 areaCard">
                <div className="text-xl sm:text-2xl">🏙️</div>
                <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mt-1 mb-2">Our South Delhi Service Areas</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 areaList">
                  {[
                    'Vasant Kunj',
                    'Shanti Niketan',
                    'Defence Colony',
                    'Greater Kailash (GK) I & II',
                    'Jor Bagh',
                    'Hauz Khas Enclave',
                    'Panchsheel Park',
                  ].map((t) => (
                    <span key={t} className="text-[10px] sm:text-xs bg-[#FAF3E8] text-[#8C5E1A] border border-[#C99E54]/30 rounded-full px-2.5 py-1 font-medium areaItem">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FDF8F0] border border-[#C99E54]/25 shadow-[0_6px_20px_rgba(191,160,110,0.10)] rounded-xl sm:rounded-2xl p-4 sm:p-5 areaCard">
                <div className="text-xl sm:text-2xl">🏘️</div>
                <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mt-1 mb-2">Our West Delhi Service Areas</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 areaList">
                  {[
                    'Punjabi Bagh',
                    'Rajouri Garden',
                    'Patel Nagar (East & West)',
                    'Janakpuri (A,B,C Blocks)',
                    'Vikaspuri',
                    'Paschim Vihar',
                    'Kirti Nagar',
                  ].map((t) => (
                    <span key={t} className="text-[10px] sm:text-xs bg-[#FAF3E8] text-[#8C5E1A] border border-[#C99E54]/30 rounded-full px-2.5 py-1 font-medium areaItem">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FDF8F0] border border-[#C99E54]/25 shadow-[0_6px_20px_rgba(191,160,110,0.10)] rounded-xl sm:rounded-2xl p-4 sm:p-5 areaCard sm:col-span-2 md:col-span-1">
                <div className="text-xl sm:text-2xl">🌆</div>
                <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mt-1 mb-2">Our NCR Service Areas</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 areaList">
                  {[
                    'Gurgaon',
                    'Noida',
                    'Faridabad',
                    'Greater Noida',
                    'Golf Course Road',
                    'DLF City',
                    'Sushant Lok 1',
                  ].map((t) => (
                    <span key={t} className="text-[10px] sm:text-xs bg-[#FAF3E8] text-[#8C5E1A] border border-[#C99E54]/30 rounded-full px-2.5 py-1 font-medium areaItem">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-6 bg-gradient-to-br from-[#FFFDF9] via-[#FDF8F0] to-[#FEFAF4] border border-[#C99E54]/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-[0_8px_25px_rgba(191,160,110,0.12)] seoBottomCard">
              <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">Why Choose DSMR for iPhone Mic & Speaker Repair in Delhi NCR?</h3>
              <div className="text-[#334155] mt-2 space-y-2 text-xs sm:text-sm leading-relaxed seoBottomText">
                <p>
                  DSMR provides fast, doorstep iPhone audio repair services across Delhi NCR. If your iPhone has low ear speaker volume, crackling loudspeaker, or the caller cannot hear you due to a broken microphone, our experts diagnose and fix it in 20 minutes right in front of you.
                </p>
                <p>
                  We use genuine OEM acoustic speaker and microphone modules with up to 1-Year Warranty across Delhi, Gurgaon, Noida, and Faridabad.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Toaster />
    </div>
  );
}
